import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import { useSubscription } from '../contexts/SubscriptionContext';
import { apiRequest } from '../lib/queryClient';
import { SubscriptionPlan } from '../types/subscription';
import { useToast } from '../hooks/use-toast';
import { Link } from 'wouter';

export default function PricingPage() {
  const { user } = useAuth();
  const { userSubscription, companySubscriptions } = useSubscription();
  const { toast } = useToast();
  const [targetType, setTargetType] = useState<'user' | 'company'>('user');
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);

  // Fetch subscription plans
  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError
  } = useQuery<SubscriptionPlan[]>({
    queryKey: ['/api/subscription-plans'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/subscription-plans');
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    },
  });

  // Fetch user's companies for company subscription toggle
  const {
    data: companies,
    isLoading: companiesLoading
  } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/users/me/companies');
      if (!res.ok) {
        if (res.status === 401) {
          // User not authenticated, return empty array
          return [];
        }
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    },
    enabled: !!user && targetType === 'company',
  });

  // Filter plans by target type
  const filteredPlans = plans?.filter(plan => plan.target_type === targetType) || [];

  // Group plans by type for display
  const monthlyPlan = filteredPlans.find(p => p.plan_type === 'monthly');
  const yearlyPlan = filteredPlans.find(p => p.plan_type === 'yearly');
  const lifetimePlan = filteredPlans.find(p => p.plan_type === 'lifetime');

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleGetStarted = (plan: SubscriptionPlan) => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to subscribe to a plan.',
        variant: 'destructive',
      });
      return;
    }

    if (targetType === 'company' && !selectedCompany) {
      toast({
        title: 'Company required',
        description: 'Please select a company for company subscription.',
        variant: 'destructive',
      });
      return;
    }

    // Redirect to checkout
    const targetId = targetType === 'user' ? user.id : selectedCompany!;
    window.location.href = `/checkout/${plan.id}?target_type=${targetType}&target_id=${targetId}`;
  };

  const getCurrentPlan = () => {
    if (targetType === 'user' && userSubscription?.has_active) {
      return userSubscription;
    }
    if (targetType === 'company' && selectedCompany && companySubscriptions[selectedCompany]?.has_active) {
      return companySubscriptions[selectedCompany];
    }
    return null;
  };

  const currentPlan = getCurrentPlan();

  if (plansLoading) {
    return (
      <div className="min-h-screen bg-app-theme flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-green-600">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="min-h-screen bg-app-theme flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-green-600 mb-4">Error Loading Plans</h2>
          <p className="text-green-600">Unable to load pricing plans. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-theme">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl md:text-3xl font-bold mb-4">
            Choose Your GHG Tracking Plan
          </h1>
          <p className="text-sm md:text-base mb-6 text-green-100">
            Track, analyze, and report your carbon emissions with our comprehensive platform
          </p>
          
          {/* Target Type Toggle */}
          {user && (
            <div className="flex justify-center mb-8">
              <div className="bg-green-700 rounded-lg p-1 flex">
                <button
                  onClick={() => setTargetType('user')}
                  className={`px-6 py-2 rounded-md transition-colors ${
                    targetType === 'user'
                      ? 'bg-white text-green-800 font-semibold'
                      : 'text-green-100 hover:text-white'
                  }`}
                >
                  Personal
                </button>
                <button
                  onClick={() => setTargetType('company')}
                  className={`px-6 py-2 rounded-md transition-colors ${
                    targetType === 'company'
                      ? 'bg-white text-green-800 font-semibold'
                      : 'text-green-100 hover:text-white'
                  }`}
                >
                  Company
                </button>
              </div>
            </div>
          )}

          {/* Company Selection */}
          {user && targetType === 'company' && companies && (
            <div className="mb-8">
              <label className="block text-lg font-semibold mb-4">Select Company:</label>
              <select
                value={selectedCompany || ''}
                onChange={(e) => setSelectedCompany(Number(e.target.value))}
                className="px-4 py-2 rounded-lg text-green-800 font-semibold"
              >
                <option value="">Choose a company...</option>
                {companies.map((company: any) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Monthly Plan */}
          {monthlyPlan && (
            <div className="bg-green-50 rounded-3xl shadow-xl p-8 border border-green-100 hover:shadow-2xl transition-all duration-300 relative">
              <div className="text-center">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-green-900 mb-2">{monthlyPlan.name}</h3>
                  <p className="text-green-600">Perfect for small teams</p>
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline justify-center">
                    <span className="text-6xl font-bold text-green-900">{formatPrice(monthlyPlan.price_cents).replace('$', '')}</span>
                    <span className="text-2xl text-green-500 ml-2">/month</span>
                  </div>
                  <p className="text-green-500 mt-2">Billed monthly</p>
                </div>
                <ul className="space-y-4 mb-8 text-left">
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Scope 1 & 2 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Scope 3 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Advanced Analytics & Reports</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Priority Support</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Auto Updates</span>
                  </li>
                </ul>
                <button
                  onClick={() => handleGetStarted(monthlyPlan)}
                  disabled={targetType === 'company' && !selectedCompany}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    currentPlan?.plan_name === monthlyPlan.name
                      ? 'bg-green-200 text-green-500 cursor-not-allowed'
                      : 'bg-green-700 hover:bg-green-800 text-white hover:shadow-lg'
                  }`}
                >
                  {currentPlan?.plan_name === monthlyPlan.name ? 'Current Plan' : 'Get Started'}
                </button>
              </div>
            </div>
          )}

          {/* Yearly Plan - Featured */}
          {yearlyPlan && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl shadow-2xl p-8 border-2 border-green-300 relative transform scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  Most Popular
                </span>
              </div>
              <div className="text-center">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-green-900 mb-2">{yearlyPlan.name}</h3>
                  <p className="text-green-600">Best value for growing teams</p>
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline justify-center">
                    <span className="text-6xl font-bold text-green-900">{formatPrice(yearlyPlan.price_cents).replace('$', '')}</span>
                    <span className="text-2xl text-green-500 ml-2">/year</span>
                  </div>
                  <div className="flex items-center justify-center mt-2">
                    <span className="text-lg text-green-600">${formatPrice(yearlyPlan.price_cents / 12).replace('$', '')}/month</span>
                    <span className="ml-2 bg-green-200 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">Save 20%</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8 text-left">
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-800 font-medium">Scope 1 & 2 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-800 font-medium">Scope 3 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-800 font-medium">Advanced Analytics & Reports</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-800 font-medium">Priority Support</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-800 font-medium">Auto Updates</span>
                  </li>
                </ul>
                <button
                  onClick={() => handleGetStarted(yearlyPlan)}
                  disabled={targetType === 'company' && !selectedCompany}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    currentPlan?.plan_name === yearlyPlan.name
                      ? 'bg-green-200 text-green-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white hover:shadow-lg'
                  }`}
                >
                  {currentPlan?.plan_name === yearlyPlan.name ? 'Current Plan' : 'Get Started'}
                </button>
              </div>
            </div>
          )}

          {/* Lifetime Plan */}
          {lifetimePlan && (
            <div className="bg-green-50 rounded-3xl shadow-xl p-8 border border-green-100 hover:shadow-2xl transition-all duration-300 relative">
              <div className="text-center">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-green-900 mb-2">{lifetimePlan.name}</h3>
                  <p className="text-green-600">One-time payment, lifetime access</p>
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline justify-center">
                    <span className="text-6xl font-bold text-green-900">{formatPrice(lifetimePlan.price_cents).replace('$', '')}</span>
                    <span className="text-2xl text-green-500 ml-2">one-time</span>
                  </div>
                  <p className="text-green-500 mt-2">No recurring charges</p>
                </div>
                <ul className="space-y-4 mb-8 text-left">
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">Scope 1 & 2 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-500">Scope 3 Emissions Tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-500">Advanced Analytics & Reports</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-500">Priority Support</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-green-700">On-Premise Installation</span>
                  </li>
                </ul>
                <button
                  onClick={() => handleGetStarted(lifetimePlan)}
                  disabled={targetType === 'company' && !selectedCompany}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    currentPlan?.plan_name === lifetimePlan.name
                      ? 'bg-green-200 text-green-500 cursor-not-allowed'
                      : 'bg-green-700 hover:bg-green-800 text-white hover:shadow-lg'
                  }`}
                >
                  {currentPlan?.plan_name === lifetimePlan.name ? 'Current Plan' : 'Get Started'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Current Plan Status */}
        {currentPlan && (
          <div className="mt-12 text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Current Plan</h3>
              <p className="text-green-700">
                You're currently on the <strong>{currentPlan.plan_name}</strong> plan
                {currentPlan.current_period_end && (
                  <span>
                    {' '}until {new Date(currentPlan.current_period_end).toLocaleDateString()}
                  </span>
                )}
              </p>
              <Link href="/settings" className="text-green-600 hover:text-green-800 underline mt-2 inline-block">
                Manage Subscription
              </Link>
            </div>
          </div>
        )}

        {/* Sign In Prompt */}
        {!user && (
          <div className="mt-12 text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Ready to Get Started?</h3>
              <p className="text-green-700 mb-4">Sign in to subscribe to a plan</p>
              <Link href="/auth" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
